/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useQuery, useLazyQuery } from "@apollo/client";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import DateFnsUtils from "@date-io/date-fns";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import {
  GET_USER_OPPORTUNITIES,
  GET_USER_OPPORTUNITIES_NEXT,
  GET_USER_OPPORTUNITIES_PREV,
} from "graphql/query/admin";
import Table from "Components/Table/Table";

const STATUS_OPTIONS = ["open", "closed", "cancelled"];
const CATEGORY_OPTIONS = [
  "Master Photographer",
  "Master Videographer",
  "Candid Photographer",
  "Cinematographer",
  "Aerial Cinematographer",
  "Editor",
  "Assistant",
  "Event Manager",
  "Other",
];
const POST_TYPE_OPTIONS = ["LOOKING_FOR_FREELANCER", "LOOKING_FOR_WORK"];

const OpportunityTab = ({ userId }) => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [pageInfo, setPageInfo] = useState("");
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ status: "", category: "", postType: "", createdAtFrom: null, createdAtTo: null });
  const [appliedFilters, setAppliedFilters] = useState({});

  const buildFilters = () => {
    const f = {};
    if (appliedFilters.status) f.status = appliedFilters.status;
    if (appliedFilters.category) f.category = appliedFilters.category;
    if (appliedFilters.postType) f.postType = appliedFilters.postType;
    if (appliedFilters.createdAtFrom) f.createdAtFrom = appliedFilters.createdAtFrom;
    if (appliedFilters.createdAtTo) f.createdAtTo = appliedFilters.createdAtTo;
    return f;
  };

  const { data, refetch, error, loading } = useQuery(GET_USER_OPPORTUNITIES, {
    variables: {
      userId,
      first: 10,
      orderBy: "createdAt_DESC",
      filters: buildFilters(),
    },
    fetchPolicy: "network-only",
    skip: !userId,
  });

  const [getNextPage, { loading: nextLoader }] = useLazyQuery(GET_USER_OPPORTUNITIES_NEXT);
  const [getPrevPage, { loading: prevLoader }] = useLazyQuery(GET_USER_OPPORTUNITIES_PREV);

  const mapEdges = (edges) => edges.map((e) => ({ ...e.node }));

  const handleNextPage = async () => {
    if (pageInfo.hasNextPage) {
      const res = await getNextPage({
        variables: { userId, first: 10, after: pageInfo.endCursor, orderBy: "createdAt_DESC", filters: buildFilters() },
        fetchPolicy: "network-only",
      });
      if (res.data?.userOpportunities) {
        setOpportunities(mapEdges(res.data.userOpportunities.edges));
        setPage(page + 1);
        setPageInfo(res.data.userOpportunities.pageInfo);
      }
    }
  };

  const handlePreviousPage = async () => {
    if (pageInfo.hasPreviousPage) {
      const res = await getPrevPage({
        variables: { userId, last: 10, before: pageInfo.startCursor, orderBy: "createdAt_DESC", filters: buildFilters() },
        fetchPolicy: "network-only",
      });
      if (res.data?.userOpportunities) {
        setOpportunities(mapEdges(res.data.userOpportunities.edges));
        setPage(page - 1);
        setPageInfo(res.data.userOpportunities.pageInfo);
      }
    }
  };

  useEffect(() => {
    if (data?.userOpportunities) {
      setOpportunities(mapEdges(data.userOpportunities.edges));
      setPageInfo(data.userOpportunities.pageInfo);
    }
    setPage(0);
  }, [data]);

  const handleApplyFilter = () => {
    setAppliedFilters({ ...filters });
    setTimeout(() => refetch(), 100);
  };

  const handleResetFilter = () => {
    setFilters({ status: "", category: "", postType: "", createdAtFrom: null, createdAtTo: null });
    setAppliedFilters({});
    setTimeout(() => refetch(), 100);
  };

  const handleViewButton = (id) => {
    const opportunity = opportunities.find((o) => o.id === id);
    navigate("/admin/user/view-opportunity", { state: { opportunityId: id, userId, opportunity } });
  };

  const TABLE_HEAD = [
    { id: "id", flag: "id", label: "No.", alignRight: false },
    { id: "title", flag: "title", label: "Title", alignRight: false },
    { id: "postType", flag: "postType", label: "Post Type", alignRight: false },
    { id: "role", flag: "role", label: "Role", alignRight: false },
    { id: "status", flag: "status", label: "Status", alignRight: false },
    { id: "location", flag: "location", label: "Location", alignRight: false },
    { id: "startDate", flag: "startDate", label: "Start Date", alignRight: false },
    { id: "endDate", flag: "endDate", label: "End Date", alignRight: false },
    { id: "createdAt", flag: "createdAt", label: "Created At", alignRight: false },
    {
      id: "actions",
      flag: { read: true },
      callback: { handleViewButton },
      label: "Actions",
      alignRight: false,
    },
  ];

  return (
    <Box style={{ padding: "16px 0" }}>
      {/* Filters */}
      <Grid container spacing={2} style={{ marginBottom: 16, padding: "0 8px" }}>
        <Grid item xs={12} sm={2}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {CATEGORY_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Post Type</InputLabel>
            <Select
              label="Post Type"
              value={filters.postType}
              onChange={(e) => setFilters({ ...filters, postType: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {POST_TYPE_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              autoOk
              label="From"
              clearable
              format="dd/MM/yyyy"
              disableFuture
              inputVariant="outlined"
              size="small"
              style={{ width: "48%", marginRight: "4%" }}
              value={filters.createdAtFrom}
              onChange={(e) => setFilters({ ...filters, createdAtFrom: e })}
            />
            <DatePicker
              autoOk
              label="To"
              clearable
              format="dd/MM/yyyy"
              disableFuture
              inputVariant="outlined"
              size="small"
              style={{ width: "48%" }}
              minDate={filters.createdAtFrom || undefined}
              value={filters.createdAtTo}
              onChange={(e) => setFilters({ ...filters, createdAtTo: e })}
            />
          </MuiPickersUtilsProvider>
        </Grid>
        <Grid item xs={12} sm={3} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Button variant="contained" color="primary" onClick={handleApplyFilter} style={{ background: "var(--color-primary, #1976d2)" }}>
            Apply
          </Button>
          <Button variant="outlined" onClick={handleResetFilter}>
            Reset
          </Button>
        </Grid>
      </Grid>

      <Table
        error={error}
        USERLIST={opportunities}
        page={page}
        handleNextPage={handleNextPage}
        handlePreviousPage={handlePreviousPage}
        TABLE_HEAD={TABLE_HEAD}
        pageInfo={pageInfo}
        loading={loading || nextLoader || prevLoader}
      />
    </Box>
  );
};

export default OpportunityTab;
